import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../convex_generated/api'
import { useToast } from '../contexts/ToastContext'
import {
  UsersIcon,
  BookOpenIcon,
  CodeBracketSquareIcon,
  FlagIcon,
  StarIcon,
  ShoppingCartIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CurrencyPoundIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import { GENRE_CHART_COLORS } from '../constants/genres'
import { asId } from '../lib/ids'
import type { Id } from '../convex_generated/dataModel'

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

const StatCard: React.FC<{
  label: string
  value: string | number
  sub?: string
  icon: React.ReactNode
  color: string
}> = ({ label, value, sub, icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3">
    <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Genre bar chart
// ---------------------------------------------------------------------------

const GENRE_COLORS: Record<string, string> = GENRE_CHART_COLORS

const GenreChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const sorted = Object.entries(data).sort((a, b) => b[1] - a[1])
  const max = Math.max(...sorted.map(([, v]) => v), 1)
  return (
    <div className="space-y-2">
      {sorted.map(([genre, count]) => (
        <div key={genre} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-right text-gray-600 dark:text-gray-400 capitalize truncate">{genre}</span>
          <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${(count / max) * 100}%`, backgroundColor: GENRE_COLORS[genre] ?? '#9ca3af' }}
            />
          </div>
          <span className="w-8 text-gray-500 dark:text-gray-400">{count}</span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Report row
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  reviewed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  dismissed: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
}

interface EnrichedReport {
  _id: string
  reporterId: string
  bookId?: string
  branchId?: string
  reason: string
  details?: string
  status: string
  createdAt: number
  reporterName: string
  bookTitle?: string
  branchTitle?: string
  illustrationId?: string
  illustrationUrl?: string
}

const ReportRow: React.FC<{
  report: EnrichedReport
  onAction: (reportId: string, status: 'reviewed' | 'resolved' | 'dismissed') => void
  onDeleteBook: (bookId: string) => void
  onDeleteBranch: (branchId: string) => void
  onDeleteIllustration: (illustrationId: string) => void
  acting: boolean
}> = ({ report, onAction, onDeleteBook, onDeleteBranch, onDeleteIllustration, acting }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[report.status]}`}>
            {report.status}
          </span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 capitalize">
            {report.reason}
          </span>
          <span className="text-xs text-gray-400">
            {new Date(report.createdAt).toLocaleDateString()} · by {report.reporterName}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
          {report.illustrationId ? 'Illustration' : report.bookId ? 'Book' : 'Branch'}: {report.bookTitle ?? 'Unknown'}
          {report.branchTitle && <span className="text-gray-500"> → {report.branchTitle}</span>}
        </p>
        {report.details && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{report.details}</p>
        )}
        {report.illustrationUrl && (
          <img
            src={report.illustrationUrl}
            alt="Reported illustration"
            className="mt-2 max-h-40 rounded-lg border border-gray-200 dark:border-gray-700"
          />
        )}
      </div>
      {report.bookId && (
        <Link
          to={`/branches/${report.bookId}`}
          className="flex-shrink-0 text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
        >
          <EyeIcon className="w-3 h-3" /> View
        </Link>
      )}
    </div>

    {report.status === 'pending' && (
      <div className="flex items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-700/50">
        <button
          disabled={acting}
          onClick={() => onAction(report._id, 'resolved')}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors font-medium"
        >
          <CheckCircleIcon className="w-3.5 h-3.5" /> Resolve
        </button>
        <button
          disabled={acting}
          onClick={() => onAction(report._id, 'dismissed')}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/40 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium"
        >
          <XCircleIcon className="w-3.5 h-3.5" /> Dismiss
        </button>
        {report.bookId && (
          <button
            disabled={acting}
            onClick={() => { if (confirm('Permanently delete this book and all its branches?')) onDeleteBook(report.bookId!) }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-medium ml-auto"
          >
            <TrashIcon className="w-3.5 h-3.5" /> Delete book
          </button>
        )}
        {report.branchId && !report.bookId && !report.illustrationId && (
          <button
            disabled={acting}
            onClick={() => { if (confirm('Permanently delete this branch?')) onDeleteBranch(report.branchId!) }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-medium ml-auto"
          >
            <TrashIcon className="w-3.5 h-3.5" /> Delete branch
          </button>
        )}
        {report.illustrationId && (
          <button
            disabled={acting}
            onClick={() => { if (confirm('Remove this illustration?')) onDeleteIllustration(report.illustrationId!) }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-medium ml-auto"
          >
            <TrashIcon className="w-3.5 h-3.5" /> Remove illustration
          </button>
        )}
      </div>
    )}
  </div>
)

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'users' | 'books' | 'reports' | 'history'
type UserFilter = 'day' | 'week' | 'month' | 'all'

const AdminDashboardPage: React.FC = () => {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [userFilter, setUserFilter] = useState<UserFilter>('month')
  const [acting, setActing] = useState(false)

  // Stats are a snapshot the backend recomputes on a schedule (null until the
  // first one exists) — ask for one if there is none yet.
  const stats = useQuery(api.admin.getDashboardStats)
  const requestDashboardRefresh = useMutation(api.admin.requestDashboardRefresh)
  const requestedInitialStats = useRef(false)
  useEffect(() => {
    if (stats === null && !requestedInitialStats.current) {
      requestedInitialStats.current = true
      requestDashboardRefresh().catch(() => {})
    }
  }, [stats, requestDashboardRefresh])

  const pendingWithdrawals = useQuery(
    api.admin.getPendingWithdrawals,
    activeTab === 'overview' ? {} : 'skip',
  )
  const settleWithdrawal = useMutation(api.admin.settleWithdrawal)
  const handleSettleWithdrawal = useCallback(async (withdrawalId: Id<'withdrawals'>, outcome: 'paid' | 'rejected') => {
    const question = outcome === 'paid'
      ? 'Mark this withdrawal as paid? Only do this if the transfer shows in Stripe.'
      : 'Refund this withdrawal? The StoryCoins go back to the author\'s balance.'
    if (!window.confirm(question)) return
    setActing(true)
    try {
      await settleWithdrawal({ withdrawalId, outcome })
      await requestDashboardRefresh()
      showToast({ message: outcome === 'paid' ? 'Withdrawal marked paid' : 'Withdrawal refunded', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Action failed', type: 'error' })
    } finally {
      setActing(false)
    }
  }, [settleWithdrawal, requestDashboardRefresh, showToast])
  const pendingReports = useQuery(api.admin.getPendingReports) as EnrichedReport[] | undefined
  const allReports = useQuery(
    api.admin.getAllReports,
    activeTab === 'history' ? {} : 'skip',
  ) as EnrichedReport[] | undefined

  const adminBooks = useQuery(
    api.admin.getAdminBooks,
    activeTab === 'books' ? {} : 'skip',
  )

  const adminUsers = useQuery(
    api.admin.getUsers,
    activeTab === 'users' ? { filter: userFilter } : 'skip',
  )

  const updateReportStatus = useMutation(api.admin.updateReportStatus)
  const deleteBookMut = useMutation(api.admin.deleteBook)
  const deleteBranchMut = useMutation(api.admin.deleteBranch)
  const deleteIllustrationMut = useMutation(api.branches.deleteIllustration)
  const reimportBookAction = useAction(api.gutenberg.reimportBook)
  const [reimporting, setReimporting] = useState<string | null>(null)

  const handleAction = useCallback(async (reportId: string, status: 'reviewed' | 'resolved' | 'dismissed') => {
    setActing(true)
    try {
      await updateReportStatus({ reportId: asId<"reports">(reportId), status })
      showToast({ message: `Report ${status}`, type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Action failed', type: 'error' })
    } finally {
      setActing(false)
    }
  }, [updateReportStatus, showToast])

  const handleDeleteBook = useCallback(async (bookId: string) => {
    setActing(true)
    try {
      await deleteBookMut({ bookId: asId<"books">(bookId) })
      showToast({ message: 'Book deleted', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Delete failed', type: 'error' })
    } finally {
      setActing(false)
    }
  }, [deleteBookMut, showToast])

  const handleDeleteBranch = useCallback(async (branchId: string) => {
    setActing(true)
    try {
      await deleteBranchMut({ branchId: asId<"branches">(branchId) })
      showToast({ message: 'Branch deleted', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Delete failed', type: 'error' })
    } finally {
      setActing(false)
    }
  }, [deleteBranchMut, showToast])

  const handleDeleteIllustration = useCallback(async (illustrationId: string) => {
    setActing(true)
    try {
      await deleteIllustrationMut({ illustrationId: asId<"illustrations">(illustrationId) })
      showToast({ message: 'Illustration removed', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Remove failed', type: 'error' })
    } finally {
      setActing(false)
    }
  }, [deleteIllustrationMut, showToast])

  const handleReimport = useCallback(async (bookId: string) => {
    setReimporting(bookId)
    try {
      const result = await reimportBookAction({ bookId: asId<"books">(bookId) })
      showToast({ message: `Re-imported successfully (${result.wordCount?.toLocaleString()} words)`, type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Re-import failed', type: 'error' })
    } finally {
      setReimporting(null)
    }
  }, [reimportBookAction, showToast])

  const pendingCount = pendingReports?.length ?? 0

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ChartBarIcon className="w-7 h-7 text-primary-500" />
            Admin Dashboard
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Platform overview and content moderation
          </p>
        </div>
        {activeTab === 'overview' && stats && (
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span>Updated {new Date(stats.computedAt).toLocaleTimeString()}</span>
            <button
              onClick={() => requestDashboardRefresh().catch(() => {})}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
            >
              <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {([
          ['overview', 'Overview'],
          ['users', 'Users'],
          ['books', 'Books'],
          ['reports', `Pending reports${pendingCount > 0 ? ` (${pendingCount})` : ''}`],
          ['history', 'Report history'],
        ] as [Tab, string][]).map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview tab ── */}
      {activeTab === 'overview' && (
        <>
          {!stats ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Top stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Total users"
                  value={stats.users.total}
                  sub={`${stats.users.activeDaily} active today · ${stats.users.activeWeekly} this week`}
                  icon={<UsersIcon className="w-5 h-5 text-blue-600" />}
                  color="bg-blue-50 dark:bg-blue-900/30"
                />
                <StatCard
                  label="Books"
                  value={stats.books.total}
                  sub={`${stats.books.last30d} added last 30d`}
                  icon={<BookOpenIcon className="w-5 h-5 text-purple-600" />}
                  color="bg-purple-50 dark:bg-purple-900/30"
                />
                <StatCard
                  label="Branches"
                  value={stats.branches.total}
                  sub={`${stats.branches.userCreated} user-created · ${stats.branches.last30d} last 30d`}
                  icon={<CodeBracketSquareIcon className="w-5 h-5 text-teal-600" />}
                  color="bg-teal-50 dark:bg-teal-900/30"
                />
                <StatCard
                  label="Pending reports"
                  value={stats.reports.pending}
                  sub={`${stats.reports.total} total reports filed`}
                  icon={<FlagIcon className="w-5 h-5 text-red-600" />}
                  color="bg-red-50 dark:bg-red-900/30"
                />
              </div>

              {/* Second row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Total words"
                  value={stats.branches.totalWordCount >= 1_000_000
                    ? `${(stats.branches.totalWordCount / 1_000_000).toFixed(1)}M`
                    : stats.branches.totalWordCount >= 1_000
                      ? `${(stats.branches.totalWordCount / 1_000).toFixed(0)}K`
                      : stats.branches.totalWordCount}
                  sub={`${stats.branches.public} public · ${stats.branches.paid} paid`}
                  icon={<BookOpenIcon className="w-5 h-5 text-amber-600" />}
                  color="bg-amber-50 dark:bg-amber-900/30"
                />
                <StatCard
                  label="Ratings given"
                  value={stats.ratings.total}
                  icon={<StarIcon className="w-5 h-5 text-yellow-600" />}
                  color="bg-yellow-50 dark:bg-yellow-900/30"
                />
                <StatCard
                  label="Purchases"
                  value={stats.purchases.total}
                  sub={`${stats.purchases.volumeSC.toLocaleString()} SC transacted`}
                  icon={<ShoppingCartIcon className="w-5 h-5 text-green-600" />}
                  color="bg-green-50 dark:bg-green-900/30"
                />
                <StatCard
                  label="Users by role"
                  value={`${stats.users.byRole.writer}W / ${stats.users.byRole.reader}R`}
                  sub={`${stats.users.byRole.artist} artists · ${stats.users.byRole.admin} admins`}
                  icon={<UsersIcon className="w-5 h-5 text-indigo-600" />}
                  color="bg-indigo-50 dark:bg-indigo-900/30"
                />
              </div>

              {/* Revenue & Financials */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <CurrencyPoundIcon className="w-5 h-5 text-green-600" />
                  Platform Revenue & Financials
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    label="Platform fees"
                    value={`£${stats.revenue.platformFeesGBP}`}
                    sub={`${stats.revenue.platformFeesSC.toLocaleString()} SC (25% of sales)`}
                    icon={<CurrencyPoundIcon className="w-5 h-5 text-green-600" />}
                    color="bg-green-50 dark:bg-green-900/30"
                  />
                  <StatCard
                    label="SC top-ups via Stripe"
                    value={`£${stats.revenue.topUpsGBP}`}
                    sub={`${stats.revenue.totalTopUpsSC.toLocaleString()} SC across ${stats.revenue.totalTopUpsCount} purchases`}
                    icon={<ShoppingCartIcon className="w-5 h-5 text-blue-600" />}
                    color="bg-blue-50 dark:bg-blue-900/30"
                  />
                  <StatCard
                    label="Paid out to authors"
                    value={`£${stats.revenue.totalWithdrawnGBP}`}
                    sub={`${stats.revenue.totalWithdrawals} withdrawals processed`}
                    icon={<BanknotesIcon className="w-5 h-5 text-amber-600" />}
                    color="bg-amber-50 dark:bg-amber-900/30"
                  />
                  <StatCard
                    label="Pending withdrawals"
                    value={stats.revenue.pendingWithdrawals}
                    sub="Awaiting settlement"
                    icon={<BanknotesIcon className="w-5 h-5 text-orange-600" />}
                    color="bg-orange-50 dark:bg-orange-900/30"
                  />
                </div>
              </div>

              {/* Withdrawals that didn't settle on their own */}
              {pendingWithdrawals && pendingWithdrawals.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Pending withdrawals</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4">
                    Payouts normally settle automatically. Check Stripe before settling one by hand.
                  </p>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {pendingWithdrawals.map((w) => (
                      <li key={w._id} className="py-3 flex flex-wrap items-center gap-3 text-sm">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {w.displayName} <span className="text-gray-400 font-normal">@{w.username}</span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            £{w.gbpAmount.toFixed(2)} · {w.storybucksAmount.toLocaleString()} SC · requested {new Date(w.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => handleSettleWithdrawal(w._id, 'paid')}
                          disabled={acting}
                          className="text-xs px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 font-medium transition-colors disabled:opacity-50"
                        >
                          Mark paid
                        </button>
                        <button
                          onClick={() => handleSettleWithdrawal(w._id, 'rejected')}
                          disabled={acting}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 font-medium transition-colors disabled:opacity-50"
                        >
                          Refund
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Genre breakdown */}
              {Object.keys(stats.books.genreCounts).length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Books by genre</h2>
                  <GenreChart data={stats.books.genreCounts} />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Users tab ── */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Filter buttons */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Joined:</span>
            {(['day', 'week', 'month', 'all'] as UserFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setUserFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  userFilter === f
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {f === 'day' ? 'Last 24 h' : f === 'week' ? 'Last 7 days' : f === 'month' ? 'Last 30 days' : 'All time'}
              </button>
            ))}
            {adminUsers && (
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                {adminUsers.length} user{adminUsers.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {!adminUsers ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
              <UsersIcon className="w-12 h-12 mx-auto mb-3" />
              <p className="text-sm">No users joined in this period.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">User</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Username</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {adminUsers.map((user) => (
                    <tr
                      key={user._id}
                      onClick={() => navigate(`/profile/${user.username}`)}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {user.avatar ? (
                            <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
                              <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                                {user.displayName?.[0]?.toUpperCase() ?? '?'}
                              </span>
                            </div>
                          )}
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {user.displayName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">@{user.username}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{user.email ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
                          user.role === 'admin' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          : user.role === 'writer' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                          : user.role === 'artist' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(user.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Books management tab ── */}
      {activeTab === 'books' && (
        <div className="space-y-3">
          {!adminBooks ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : adminBooks.length === 0 ? (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
              <BookOpenIcon className="w-12 h-12 mx-auto mb-3" />
              <p className="text-sm">No books found.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Title</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Author</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Source</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Date</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {adminBooks.map((book) => {
                    const canReimport = !!book.gutenbergId || !!book.sourceUrl
                    const isReimporting = reimporting === book._id
                    return (
                      <tr key={book._id} className="hover:bg-gray-50 dark:hover:bg-gray-750/50">
                        <td className="px-4 py-3">
                          <Link to={`/branches/${book._id}`} className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                            {book.title}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{book.originalAuthor}</td>
                        <td className="px-4 py-3">
                          {book.gutenbergId ? (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                              Gutenberg #{book.gutenbergId}
                            </span>
                          ) : book.sourceUrl ? (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                              URL import
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                              Original
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                          {new Date(book.importedAt ?? book.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                          {canReimport && (
                            <button
                              disabled={!!reimporting || acting}
                              onClick={() => handleReimport(book._id)}
                              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors font-medium disabled:opacity-50"
                            >
                              <ArrowPathIcon className={`w-3.5 h-3.5 ${isReimporting ? 'animate-spin' : ''}`} />
                              {isReimporting ? 'Re-importing…' : 'Re-import'}
                            </button>
                          )}
                          <button
                            disabled={acting || !!reimporting}
                            onClick={() => { if (confirm(`Permanently delete "${book.title}" and all its branches?`)) handleDeleteBook(book._id) }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-medium disabled:opacity-50"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Pending reports tab ── */}
      {activeTab === 'reports' && (
        <div className="space-y-3">
          {!pendingReports ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : pendingReports.length === 0 ? (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-lg font-medium">All clear!</p>
              <p className="text-sm">No pending reports to review.</p>
            </div>
          ) : (
            pendingReports.map(r => (
              <ReportRow
                key={r._id}
                report={r}
                onAction={handleAction}
                onDeleteBook={handleDeleteBook}
                onDeleteBranch={handleDeleteBranch}
                onDeleteIllustration={handleDeleteIllustration}
                acting={acting}
              />
            ))
          )}
        </div>
      )}

      {/* ── Report history tab ── */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {!allReports ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : allReports.length === 0 ? (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-3" />
              <p className="text-sm">No reports have been filed yet.</p>
            </div>
          ) : (
            allReports.map(r => (
              <ReportRow
                key={r._id}
                report={r}
                onAction={handleAction}
                onDeleteBook={handleDeleteBook}
                onDeleteBranch={handleDeleteBranch}
                onDeleteIllustration={handleDeleteIllustration}
                acting={acting}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default AdminDashboardPage
